import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { toast } from 'sonner';
import LanguageSelector from '@/components/LanguageSelector';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { ArrowRight, ShieldCheck, AlertTriangle, Clock, Calendar, Users, ChevronRight, X, Check, Phone, Plus, Trash2, Mail, Key, BookOpen } from 'lucide-react';
import NormsModal from '@/components/NormsModal';
import { ACTIVITY_NORMS, FREE_ACCESS_ACTIVITIES, FREE_ACCESS_NORMS } from '@/data/activityNorms';

export default function ScanPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading, login, register, api, refreshUser } = useAuth();
  const { t } = useI18n();

  const qrToken = searchParams.get('t') || '';

  const [phase, setPhase] = useState('validating');
  const [errorMsg, setErrorMsg] = useState('');
  const [qrData, setQrData] = useState(null);
  const [qrValidated, setQrValidated] = useState(false);
  const decidedRef = useRef(false);

  // Registration form
  const [parentName, setParentName] = useState('');
  const [dni, setDni] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [children, setChildren] = useState([{ name: '', age: '' }]);
  const [submitting, setSubmitting] = useState(false);

  // OTP shared state
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [debugCode, setDebugCode] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);

  // Phone recognition flow
  const [recognizeOtpSent, setRecognizeOtpSent] = useState(false);
  const [checkingPhone, setCheckingPhone] = useState(false);

  // Daily OTP flow
  const [dailyOtpSent, setDailyOtpSent] = useState(false);

  // Daily attendees selection (who's here today)
  const [attendeesFamily, setAttendeesFamily] = useState(null);
  const [dailySelectedChildIds, setDailySelectedChildIds] = useState([]);
  const [dailyGuests, setDailyGuests] = useState([]);
  const [submittingDaily, setSubmittingDaily] = useState(false);
  const [debugPin, setDebugPin] = useState('');

  // Cuenta atrás anti-duplicados: tras enviar un OTP, el botón queda
  // deshabilitado 60 s (mismo cooldown que aplica el backend).
  const [otpCooldown, setOtpCooldown] = useState(0);
  useEffect(() => {
    if (otpCooldown <= 0) return;
    const timer = setTimeout(() => setOtpCooldown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [otpCooldown]);

  // Daily PIN re-entry
  const [dailyPinInput, setDailyPinInput] = useState('');
  const [pinPhone, setPinPhone] = useState('');
  const [resendingPin, setResendingPin] = useState(false);
  const [debugResendPin, setDebugResendPin] = useState('');

  // Login form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Workshops
  const [workshops, setWorkshops] = useState([]);
  const [dailyLimit, setDailyLimit] = useState(2);
  const [reservationsToday, setReservationsToday] = useState(0);
  const [canBook, setCanBook] = useState(true);
  const [family, setFamily] = useState(null);
  const [myReservations, setMyReservations] = useState([]);
  const [bookingId, setBookingId] = useState(null);
  const [selectedChildIds, setSelectedChildIds] = useState([]);
  const [normsOpen, setNormsOpen] = useState(null); // { title, norms }

  const goToWizard = useCallback(async () => {
    if (qrToken) {
      try {
        await api.post('/qr/activate-session', { token: qrToken });
      } catch (e) {
        console.warn('activate-session (wizard):', e?.response?.data?.detail || e.message);
      }
    }
    navigate('/register', { replace: true });
  }, [api, qrToken, navigate]);

  const activateAndLoadWorkshops = useCallback(async () => {
    try {
      await api.post('/qr/activate-session', { token: qrToken });
      const [wsData, famRes, resRes] = await Promise.all([
        api.get('/qr/workshops-today'),
        api.get('/families/me'),
        api.get('/reservations'),
      ]);
      setWorkshops(wsData.data.workshops);
      setDailyLimit(wsData.data.daily_limit);
      setReservationsToday(wsData.data.reservations_today);
      setCanBook(wsData.data.can_book);
      setFamily(famRes.data);
      setMyReservations(resRes.data || []);
      setPhase('workshops');
    } catch (err) {
      console.error('Activate session error:', err);
      if (err.response?.status === 404) { navigate('/family', { replace: true }); return; }
      setPhase('invalid');
      setErrorMsg(err.response?.data?.detail || 'Error al cargar talleres');
    }
  }, [api, qrToken, navigate]);

  // Effect 1: validate QR token
  useEffect(() => {
    if (!qrToken) {
      setPhase('invalid');
      setErrorMsg('QR invalido. Escanea el codigo en la entrada del parque.');
      return;
    }
    (async () => {
      try {
        const { data } = await api.post('/qr/validate', { token: qrToken });
        setQrData(data);
        sessionStorage.setItem('qr_token', qrToken);
        setQrValidated(true);
      } catch (err) {
        setPhase('invalid');
        setErrorMsg(err.response?.data?.detail || 'QR invalido o fuera de horario');
      }
    })();
  }, [qrToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect 2: decide phase once QR is validated AND auth is resolved.
  // With qr_session active → re-entry same device, goes straight to workshops.
  // Without qr_session → need daily registration (OTP + attendees + PIN email) OR PIN re-entry.
  useEffect(() => {
    if (!qrValidated || loading || decidedRef.current) return;
    decidedRef.current = true;
    if (user) {
      (async () => {
        try {
          const { data: sess } = await api.get('/qr/session');
          if (sess.active) {
            user.registration_complete
              ? await activateAndLoadWorkshops()
              : await goToWizard();
          } else {
            // No day-pass yet.
            // Gate 1: registro incompleto → wizard antes de cualquier flujo diario.
            if (!user.registration_complete) {
              await goToWizard();
              return;
            }
            // Check if family already has today's attendance (other device)
            try {
              const { data: fam } = await api.get('/families/me');
              if (fam.phone) setPhone(fam.phone);
              setAttendeesFamily(fam);
              setDailySelectedChildIds(fam.children?.filter(c => c.id).map(c => c.id) || []);
              if (fam.phone) {
                const { data: ds } = await api.get('/qr/check-daily-status', { params: { phone: fam.phone } });
                if (ds.status === 'has_today') {
                  setPinPhone(fam.phone);
                  setPhase('daily_pin');
                  return;
                }
              }
            } catch {}
            setPhase('otp_daily');
          }
        } catch {
          setPhase('phone_recognize');
        }
      })();
    } else {
      setPhase('phone_recognize');
    }
  }, [qrValidated, loading, user, api, activateAndLoadWorkshops, goToWizard]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Children management (registration form) ──────────────────────────────
  const addChild = () => { if (children.length < 3) setChildren([...children, { name: '', age: '' }]); };
  const removeChild = (i) => setChildren(children.filter((_, idx) => idx !== i));
  const updateChild = (i, field, val) => {
    const updated = [...children];
    updated[i] = { ...updated[i], [field]: val };
    setChildren(updated);
  };

  // ── OTP for registration flow ─────────────────────────────────────────────
  const handleSendOtp = async () => {
    if (!phone.trim()) { toast.error(t('phone_title')); return; }
    setSendingOtp(true);
    try {
      const { data } = await api.post('/auth/send-otp-public', { phone });
      setOtpSent(true);
      setOtpCooldown(60);
      setDebugCode(data.debug_code || '');
      toast.success(t('sms_sent'));
    } catch (err) {
      if (err.response?.status === 429) setOtpCooldown(60);
      toast.error(err.response?.data?.detail || 'Error al enviar SMS');
    }
    setSendingOtp(false);
  };

  const handleVerifyOtp = async () => {
    if (otp.length < 6) { toast.error(t('verify_code')); return; }
    setSendingOtp(true);
    try {
      await api.post('/auth/verify-otp-public', { phone, code: otp });
      setPhoneVerified(true);
      toast.success(t('phone_verified'));
    } catch (err) { toast.error(err.response?.data?.detail || 'Codigo invalido'); }
    setSendingOtp(false);
  };

  // ── Phone check: bifurca antes de mandar OTP ─────────────────────────────
  const handleCheckPhone = async () => {
    if (!phone.trim()) { toast.error('Introduce tu teléfono'); return; }
    setCheckingPhone(true);
    try {
      const { data } = await api.get('/qr/check-daily-status', { params: { phone } });
      if (data.status === 'has_today') {
        // Ya tiene inscripción de hoy → pedir PIN directamente, sin OTP
        setPinPhone(phone);
        setPhase('daily_pin');
      } else if (data.status === 'family_exists') {
        // Familia existe pero sin inscripción hoy → enviar OTP para nueva inscripción
        const { data: otpData } = await api.post('/auth/send-otp-public', { phone });
        setRecognizeOtpSent(true);
        setOtpCooldown(60);
        setDebugCode(otpData.debug_code || '');
        toast.success('Código enviado por WhatsApp');
      } else {
        // Teléfono no registrado → registro completo (OTP de verificación dentro de register)
        setPhase('register');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al verificar el teléfono');
    }
    setCheckingPhone(false);
  };

  // ── OTP for phone recognition (returning family, no token) ────────────────
  const handleSendOtpRecognize = async () => {
    if (!phone.trim()) { toast.error('Introduce tu teléfono'); return; }
    setSendingOtp(true);
    try {
      const { data } = await api.post('/auth/send-otp-public', { phone });
      setRecognizeOtpSent(true);
      setOtpCooldown(60);
      setDebugCode(data.debug_code || '');
      toast.success('Código enviado');
    } catch (err) {
      if (err.response?.status === 429) setOtpCooldown(60);
      toast.error(err.response?.data?.detail || 'Error al enviar el código');
    }
    setSendingOtp(false);
  };

  const handleVerifyOtpRecognize = async () => {
    if (otp.length < 6) { toast.error('Introduce el código completo'); return; }
    setSendingOtp(true);
    try {
      const { data } = await api.post('/auth/login-by-phone', { phone, code: otp });
      localStorage.setItem('clubdemanri_token', data.token);
      await refreshUser();
      if (!data.user.registration_complete) { await goToWizard(); return; }
      // Check if day-pass already active (re-entry from different browser)
      try {
        const { data: sess } = await api.get('/qr/session');
        if (sess.active) { await activateAndLoadWorkshops(); return; }
      } catch {}
      // No day-pass — phone OTP was just done via login, proceed to attendees
      try {
        const { data: fam } = await api.get('/families/me');
        setAttendeesFamily(fam);
        setDailySelectedChildIds(fam.children?.filter(c => c.id).map(c => c.id) || []);
      } catch {}
      setPhase('daily_attendees');
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || '';
      if (status === 404 || detail === 'phone_not_found') {
        setPhoneVerified(true);
        setOtp('');
        setPhase('register');
        toast.info('No encontramos una cuenta con ese número. Completa tu registro.');
      } else {
        toast.error(detail || 'Error al verificar el código');
      }
    }
    setSendingOtp(false);
  };

  // ── Daily OTP (JWT user, new day) ─────────────────────────────────────────
  const handleSendOtpDaily = async () => {
    if (!phone.trim()) { toast.error('Introduce tu teléfono'); return; }
    setSendingOtp(true);
    try {
      const { data } = await api.post('/auth/send-otp-public', { phone });
      setDailyOtpSent(true);
      setOtpCooldown(60);
      setDebugCode(data.debug_code || '');
      toast.success('Código enviado');
    } catch (err) {
      if (err.response?.status === 429) setOtpCooldown(60);
      toast.error(err.response?.data?.detail || 'Error al enviar el código');
    }
    setSendingOtp(false);
  };

  const handleVerifyOtpDaily = async () => {
    if (otp.length < 6) { toast.error('Introduce el código completo'); return; }
    setSendingOtp(true);
    try {
      // Verify OTP — marks it as verified in DB (used later by daily-complete)
      await api.post('/auth/verify-otp-public', { phone, code: otp });
      setOtp('');
      setDailyOtpSent(false);
      toast.success('Teléfono verificado');
      setPhase('daily_attendees');
    } catch (err) { toast.error(err.response?.data?.detail || 'Código inválido'); }
    setSendingOtp(false);
  };

  // ── Daily attendees & completion ──────────────────────────────────────────
  const toggleDailyChild = (cid) => {
    setDailySelectedChildIds(prev =>
      prev.includes(cid) ? prev.filter(id => id !== cid) : [...prev, cid]
    );
  };

  const addGuest = () => setDailyGuests(prev => [...prev, { name: '' }]);
  const removeGuest = (i) => setDailyGuests(prev => prev.filter((_, idx) => idx !== i));
  const updateGuest = (i, val) => {
    setDailyGuests(prev => prev.map((g, idx) => idx === i ? { name: val } : g));
  };

  const handleCompleteDailyRegistration = async () => {
    const familyData = attendeesFamily;
    const selectedChildren = (familyData?.children || [])
      .filter(c => c.id && dailySelectedChildIds.includes(c.id))
      .map(c => ({ type: 'child', id: c.id, name: c.name }));
    const validGuests = dailyGuests
      .filter(g => g.name.trim())
      .map(g => ({ type: 'guest', name: g.name.trim() }));
    const attendees = [...selectedChildren, ...validGuests];

    if (attendees.length === 0) {
      toast.error('Selecciona al menos una persona para registrar');
      return;
    }

    setSubmittingDaily(true);
    try {
      const { data } = await api.post('/qr/daily-complete', { attendees, phone });
      if (data.debug_pin) {
        setDebugPin(data.debug_pin);
        toast.success(`Pase activado. PIN de prueba: ${data.debug_pin}`);
      } else {
        toast.success('¡Pase del día activado! Revisa tu email para el PIN.');
      }
      await activateAndLoadWorkshops();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || '';
      if (status === 403 && detail.toLowerCase().includes('registro')) {
        // Registro incompleto — llevar al wizard con mensaje claro
        toast.error('Completa tu registro y los consentimientos antes de acceder.');
        await goToWizard();
      } else if (detail.includes('OTP requerido')) {
        toast.error('El código ha caducado. Vuelve a verificar el teléfono.');
        setDailyOtpSent(false);
        setOtp('');
        setPhase('otp_daily');
      } else {
        toast.error(detail || 'Error al activar el pase del día');
      }
    }
    setSubmittingDaily(false);
  };

  // ── Daily PIN re-entry ────────────────────────────────────────────────────
  const handleVerifyDailyPin = async () => {
    if (dailyPinInput.length < 6) { toast.error('Introduce el PIN completo'); return; }
    setSubmittingDaily(true);
    try {
      const { data } = await api.post('/qr/daily-verify-pin', { phone: pinPhone || phone, pin: dailyPinInput });
      localStorage.setItem('clubdemanri_token', data.token);
      await refreshUser();
      if (data.user.registration_complete) {
        await activateAndLoadWorkshops();
      } else {
        await goToWizard();
      }
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail === 'no_attendance_today') {
        toast.error('No hay inscripción para hoy. Verifica tu teléfono con WhatsApp.');
        setPhase('phone_recognize');
      } else {
        toast.error(detail || 'PIN incorrecto');
      }
    }
    setSubmittingDaily(false);
  };

  const handleResendPin = async () => {
    const phoneForResend = pinPhone || phone;
    if (!phoneForResend.trim()) { toast.error('Introduce tu teléfono'); return; }
    setResendingPin(true);
    try {
      const { data } = await api.post('/qr/daily-resend-pin', { phone: phoneForResend });
      if (data.debug_pin) {
        setDebugResendPin(data.debug_pin);
        toast.success(`PIN reenviado. PIN de prueba: ${data.debug_pin}`);
      } else {
        toast.success('PIN reenviado al email registrado');
      }
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail === 'no_attendance_today') {
        toast.error('No hay inscripción para hoy. Verifica primero con WhatsApp.');
        setPhase('phone_recognize');
      } else {
        toast.error(detail || 'Error al reenviar el PIN');
      }
    }
    setResendingPin(false);
  };

  // ── Full registration ─────────────────────────────────────────────────────
  const handleRegister = async (e) => {
    e.preventDefault();
    if (!phoneVerified) { toast.error(t('verify_first')); return; }
    if (!parentName.trim()) { toast.error(t('parent_required')); return; }
    if (!dni.trim()) { toast.error(t('dni_required')); return; }
    if (!email.trim() || !password.trim()) { toast.error(t('email_password_required')); return; }
    const validChildren = children.filter(c => c.name.trim() && c.age);
    if (validChildren.length === 0) { toast.error(t('add_one_child')); return; }
    setSubmitting(true);
    try {
      await register(email, password, parentName);
      await api.put('/families/me', {
        family_name: parentName, room_number: '', dni,
        children: validChildren.map(c => ({ name: c.name, age: parseInt(c.age, 10) })),
      });
      await api.patch('/families/me/phone', { phone, verified: true });
      toast.success(t('account_created'));
      await goToWizard();
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail.toLowerCase().includes('already registered') || detail.toLowerCase().includes('ya existe')) {
        setPhase('phone_recognize');
        setRecognizeOtpSent(false);
        setOtp('');
        toast.error('Este email ya está registrado. Accede identificándote con tu teléfono.');
      } else {
        toast.error(detail || t('error'));
      }
    }
    setSubmitting(false);
  };

  // ── Login (email + password) ──────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await login(loginEmail, loginPassword);
      toast.success(`${t('welcome')}, ${u.name}`);
      if (u.role !== 'family') { navigate(u.role === 'admin' ? '/admin' : '/monitor'); return; }
      if (!u.registration_complete) { await goToWizard(); return; }
      // Check day-pass
      try {
        const { data: sess } = await api.get('/qr/session');
        if (sess.active) { await activateAndLoadWorkshops(); return; }
      } catch {}
      // New day — need daily OTP (email login doesn't verify phone)
      try {
        const { data: fam } = await api.get('/families/me');
        if (fam.phone) setPhone(fam.phone);
        setAttendeesFamily(fam);
        setDailySelectedChildIds(fam.children?.filter(c => c.id).map(c => c.id) || []);
      } catch {}
      setPhase('otp_daily');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('invalid_credentials'));
    }
    setSubmitting(false);
  };

  // ── Workshop booking ──────────────────────────────────────────────────────
  const handleBook = async () => {
    if (selectedChildIds.length === 0) { toast.error(t('select_one_child')); return; }
    try {
      await api.post('/reservations', { workshop_id: bookingId, child_ids: selectedChildIds });
      toast.success(t('booking_confirmed'));
      setBookingId(null);
      setSelectedChildIds([]);
      const [wsData, resRes] = await Promise.all([
        api.get('/qr/workshops-today'),
        api.get('/reservations'),
      ]);
      setWorkshops(wsData.data.workshops);
      setReservationsToday(wsData.data.reservations_today);
      setCanBook(wsData.data.can_book);
      setMyReservations(resRes.data || []);
    } catch (err) { toast.error(err.response?.data?.detail || t('error')); }
  };

  const toggleChildId = (cid) => {
    setSelectedChildIds(prev =>
      prev.includes(cid) ? prev.filter(id => id !== cid) : [...prev, cid]
    );
  };

  const bookingWorkshop = useMemo(
    () => workshops.find(w => w.id === bookingId),
    [workshops, bookingId]
  );

  const isChildDisabled = useCallback((child) => {
    if (!child.id || !bookingWorkshop) return false;
    const isDuplicate = myReservations.some(r =>
      r.workshop_title === bookingWorkshop.title &&
      r.workshop_date  === bookingWorkshop.date &&
      (r.children || []).some(c => c.id === child.id)
    );
    if (isDuplicate) return true;
    const todayCount = myReservations.filter(r =>
      r.workshop_date === bookingWorkshop.date &&
      (r.children || []).some(c => c.id === child.id)
    ).length;
    return todayCount >= dailyLimit;
  }, [myReservations, bookingWorkshop, dailyLimit]);

  const goToDashboard = () => navigate('/family');

  // ── Shared header ─────────────────────────────────────────────────────────
  const Header = () => (
    <div className="text-center mb-6 relative">
      <div className="absolute right-0 top-0"><LanguageSelector /></div>
      <img src="/logo.png" alt="Logo" className="h-14 w-auto mx-auto mb-2" />
      <div className="flex items-center justify-center gap-2">
        <ShieldCheck size={18} className="text-[#27AE60]" />
        <span className="text-sm text-[#27AE60] font-medium">QR Verificado</span>
      </div>
    </div>
  );

  // ── VALIDATING ────────────────────────────────────────────────────────────
  if (phase === 'validating') {
    return (
      <div className="min-h-screen bg-[#FBF8F3] flex items-center justify-center" data-testid="scan-validating">
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-[#48293A] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-manrique-grey">Validando codigo QR...</p>
        </div>
      </div>
    );
  }

  // ── INVALID ───────────────────────────────────────────────────────────────
  if (phase === 'invalid') {
    return (
      <div className="min-h-screen bg-[#FBF8F3] flex items-center justify-center p-4" data-testid="scan-invalid">
        <div className="card-manrique max-w-md w-full text-center p-10 space-y-4">
          <div className="w-16 h-16 rounded-full bg-[#48293A]/10 flex items-center justify-center mx-auto">
            <AlertTriangle size={32} className="text-[#48293A]" />
          </div>
          <h2 className="font-heading font-bold text-2xl text-manrique-black">QR No Valido</h2>
          <p className="text-manrique-grey">{errorMsg}</p>
          <button onClick={() => { localStorage.removeItem('clubdemanri_token'); window.location.href = '/'; }} className="btn-manrique">
            Cerrar
          </button>
        </div>
      </div>
    );
  }

  // ── DAILY OTP — known family (has JWT) but no day-pass yet ───────────────
  if (phase === 'otp_daily') {
    return (
      <div className="min-h-screen bg-[#FBF8F3] flex items-center justify-center p-4" data-testid="scan-otp-daily">
        <div className="w-full max-w-md">
          <Header />
          <div className="card-manrique p-8 space-y-5">
            <div>
              <h2 className="font-heading font-bold text-2xl text-manrique-black">Pase del día</h2>
              <p className="text-manrique-grey text-sm mt-1">Verifica tu teléfono para activar el acceso de hoy.</p>
            </div>

            {!dailyOtpSent ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-manrique-black mb-1">Tu teléfono</label>
                  <div className="flex gap-3">
                    <input
                      value={phone}
                      onChange={e => setPhone(e.target.value)}
                      placeholder="+34 612 345 678"
                      className="input-manrique flex-1"
                      data-testid="daily-otp-phone-input"
                    />
                    <button
                      type="button"
                      onClick={handleSendOtpDaily}
                      disabled={sendingOtp || otpCooldown > 0 || !phone.trim()}
                      className="btn-manrique whitespace-nowrap text-sm"
                      data-testid="daily-otp-send"
                    >
                      {sendingOtp ? 'Enviando...' : otpCooldown > 0 ? `Reenviar en ${otpCooldown}s` : 'Enviar SMS'}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4 animate-fade-in">
                <p className="text-sm text-manrique-grey">Código enviado a <strong>{phone}</strong></p>
                {debugCode && (
                  <div className="bg-[#48293A]/5 border border-[#48293A]/20 rounded-2xl p-3 text-center">
                    <p className="text-xs text-manrique-grey">Código de prueba:</p>
                    <p className="font-heading font-bold text-xl text-[#48293A]" data-testid="daily-otp-debug-code">{debugCode}</p>
                  </div>
                )}
                <div className="flex justify-center">
                  <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="daily-otp-input">
                    <InputOTPGroup>
                      {[0,1,2,3,4,5].map(i => (
                        <InputOTPSlot key={i} index={i} className="w-11 h-12 text-lg rounded-xl border-black/10" />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <button
                  type="button"
                  onClick={handleVerifyOtpDaily}
                  disabled={sendingOtp || otp.length < 6}
                  className="btn-manrique w-full"
                  data-testid="daily-otp-verify"
                >
                  {sendingOtp ? 'Verificando...' : 'Verificar y continuar'}
                </button>
                <button
                  type="button"
                  onClick={() => { setDailyOtpSent(false); setOtp(''); }}
                  className="w-full text-sm text-manrique-muted hover:text-manrique-black"
                >
                  Cambiar teléfono
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── DAILY ATTENDEES — who's here today ───────────────────────────────────
  if (phase === 'daily_attendees') {
    const famChildren = attendeesFamily?.children || [];
    return (
      <div className="min-h-screen bg-[#FBF8F3] flex items-center justify-center p-4" data-testid="scan-daily-attendees">
        <div className="w-full max-w-md">
          <Header />
          <div className="card-manrique p-8 space-y-6">
            <div>
              <h2 className="font-heading font-bold text-2xl text-manrique-black">¿Quién viene hoy?</h2>
              <p className="text-manrique-grey text-sm mt-1">
                Confirma quién asiste hoy al parque. Recibirás un PIN por email para re-entrar sin WhatsApp.
              </p>
            </div>

            {/* Registered children */}
            {famChildren.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-manrique-muted uppercase tracking-wide">Menores registrados</p>
                {famChildren.map(child => {
                  const checked = dailySelectedChildIds.includes(child.id);
                  return (
                    <label
                      key={child.id}
                      className={`flex items-center gap-3 p-4 rounded-2xl border cursor-pointer transition-all ${
                        checked
                          ? 'border-[#48293A] bg-[#48293A]/5'
                          : 'border-black/10 hover:border-black/20'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleDailyChild(child.id)}
                        className="w-5 h-5 rounded accent-[#48293A]"
                      />
                      <div className="flex-1">
                        <span className="font-medium text-manrique-black">{child.name}</span>
                        <span className="text-manrique-muted text-sm ml-2">{child.age} años</span>
                      </div>
                      {checked && <Check size={16} className="text-[#48293A]" />}
                    </label>
                  );
                })}
              </div>
            )}

            {/* Temporary guests */}
            {dailyGuests.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-manrique-muted uppercase tracking-wide">Acompañantes</p>
                {dailyGuests.map((g, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <input
                      value={g.name}
                      onChange={e => updateGuest(i, e.target.value)}
                      placeholder="Nombre del acompañante"
                      className="input-manrique flex-1"
                    />
                    <button
                      type="button"
                      onClick={() => removeGuest(i)}
                      className="p-2 text-manrique-muted hover:text-[#48293A] rounded-full hover:bg-[#48293A]/5"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={addGuest}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border-2 border-dashed border-[#48293A]/30 text-[#48293A] font-medium text-sm hover:bg-[#48293A]/5 transition-colors"
            >
              <Plus size={16} /> Añadir acompañante (sobrino, familiar...)
            </button>

            <button
              type="button"
              onClick={handleCompleteDailyRegistration}
              disabled={submittingDaily || (dailySelectedChildIds.length === 0 && dailyGuests.filter(g => g.name.trim()).length === 0)}
              className="btn-manrique w-full flex items-center justify-center gap-2 py-4 disabled:opacity-40"
              data-testid="daily-attendees-confirm"
            >
              {submittingDaily
                ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <><Mail size={18} /> Confirmar y recibir PIN</>
              }
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── DAILY PIN — re-entry via email PIN ────────────────────────────────────
  if (phase === 'daily_pin') {
    return (
      <div className="min-h-screen bg-[#FBF8F3] flex items-center justify-center p-4" data-testid="scan-daily-pin">
        <div className="w-full max-w-md">
          <Header />
          <div className="card-manrique p-8 space-y-5">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Key size={20} className="text-[#48293A]" />
                <h2 className="font-heading font-bold text-2xl text-manrique-black">PIN del email</h2>
              </div>
              <p className="text-manrique-grey text-sm">
                Introduce el PIN de 6 dígitos que recibiste esta mañana en tu correo.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-manrique-black mb-1">Teléfono</label>
              <input
                value={pinPhone || phone}
                onChange={e => setPinPhone(e.target.value)}
                placeholder="+34 612 345 678"
                className="input-manrique"
              />
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-medium text-manrique-black">PIN del email</label>
              <div className="flex justify-center">
                <InputOTP maxLength={6} value={dailyPinInput} onChange={setDailyPinInput} data-testid="daily-pin-input">
                  <InputOTPGroup>
                    {[0,1,2,3,4,5].map(i => (
                      <InputOTPSlot key={i} index={i} className="w-11 h-12 text-lg rounded-xl border-black/10" />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              {debugResendPin && (
                <div className="bg-[#48293A]/5 border border-[#48293A]/20 rounded-2xl p-3 text-center">
                  <p className="text-xs text-manrique-grey">Nuevo PIN de prueba:</p>
                  <p className="font-heading font-bold text-xl text-[#48293A]">{debugResendPin}</p>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={handleVerifyDailyPin}
              disabled={submittingDaily || dailyPinInput.length < 6}
              className="btn-manrique w-full"
              data-testid="daily-pin-verify"
            >
              {submittingDaily ? 'Verificando...' : 'Entrar con PIN'}
            </button>

            <div className="text-center space-y-2 pt-1">
              <button
                type="button"
                onClick={handleResendPin}
                disabled={resendingPin}
                className="text-sm text-[#48293A] hover:underline font-medium"
                data-testid="daily-pin-resend"
              >
                {resendingPin ? 'Reenviando...' : '¿No te llega el PIN? Reenviar al email'}
              </button>
              <br />
              <button
                type="button"
                onClick={() => setPhase('phone_recognize')}
                className="text-sm text-manrique-muted hover:text-manrique-black"
              >
                Verificar con WhatsApp en su lugar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── PHONE RECOGNIZE ───────────────────────────────────────────────────────
  if (phase === 'phone_recognize') {
    return (
      <div className="min-h-screen bg-[#FBF8F3] flex items-center justify-center p-4" data-testid="scan-phone-recognize">
        <div className="w-full max-w-md">
          <Header />
          <div className="card-manrique p-8 space-y-5">
            <div>
              <h2 className="font-heading font-bold text-2xl text-manrique-black">Entra al club</h2>
              <p className="text-manrique-grey text-sm mt-1">Introduce tu teléfono y continuamos según tu situación.</p>
            </div>

            {!recognizeOtpSent ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-manrique-black mb-1">Teléfono</label>
                  <input
                    value={phone}
                    onChange={e => { setPhone(e.target.value); }}
                    onKeyDown={e => e.key === 'Enter' && handleCheckPhone()}
                    placeholder="+34 612 345 678"
                    className="input-manrique"
                    data-testid="recognize-phone-input"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleCheckPhone}
                  disabled={checkingPhone || !phone.trim()}
                  className="btn-manrique w-full flex items-center justify-center gap-2"
                  data-testid="recognize-continue"
                >
                  {checkingPhone
                    ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    : <><ArrowRight size={18} /> Continuar</>
                  }
                </button>
                <div className="text-center pt-1">
                  <button
                    onClick={() => setPhase('register')}
                    className="text-sm text-manrique-grey hover:text-[#48293A]"
                    data-testid="recognize-go-register"
                  >
                    Primera vez en el club → Registrarme
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4 animate-fade-in">
                <p className="text-sm text-manrique-grey">Código enviado a <strong>{phone}</strong></p>
                {debugCode && (
                  <div className="bg-[#48293A]/5 border border-[#48293A]/20 rounded-2xl p-3 text-center">
                    <p className="text-xs text-manrique-grey">Código de prueba:</p>
                    <p className="font-heading font-bold text-xl text-[#48293A]" data-testid="recognize-debug-code">{debugCode}</p>
                  </div>
                )}
                <div className="flex justify-center">
                  <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="recognize-otp-input">
                    <InputOTPGroup>
                      {[0,1,2,3,4,5].map(i => (
                        <InputOTPSlot key={i} index={i} className="w-11 h-12 text-lg rounded-xl border-black/10" />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <button
                  type="button"
                  onClick={handleVerifyOtpRecognize}
                  disabled={sendingOtp || otp.length < 6}
                  className="btn-manrique w-full"
                  data-testid="recognize-verify-otp"
                >
                  {sendingOtp ? 'Verificando...' : 'Verificar y continuar'}
                </button>
                <button
                  type="button"
                  onClick={() => { setRecognizeOtpSent(false); setOtp(''); }}
                  className="w-full text-sm text-manrique-muted hover:text-manrique-black"
                >
                  Cambiar teléfono
                </button>
              </div>
            )}
          </div>

          <div className="mt-4 text-center">
            <button
              onClick={() => setPhase('login')}
              className="text-sm text-manrique-grey hover:text-[#48293A]"
              data-testid="recognize-go-login"
            >
              Acceder con email y contraseña
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── REGISTER (Full registration) ──────────────────────────────────────────
  if (phase === 'register') {
    return (
      <div className="min-h-screen bg-[#FBF8F3] py-6 px-4" data-testid="scan-register">
        <div className="w-full max-w-lg mx-auto">
          <Header />
          <form onSubmit={handleRegister} className="space-y-4">
            <div className="card-manrique p-6 space-y-4">
              <h2 className="font-heading font-bold text-lg text-manrique-black">{t('register_title')}</h2>
              <div>
                <label className="block text-sm font-medium text-manrique-black mb-1">{t('parent_name_label')} <span className="text-[#48293A]">*</span></label>
                <input value={parentName} onChange={e => setParentName(e.target.value)} className="input-manrique" required placeholder="Nombre completo" data-testid="scan-parent-name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-manrique-black mb-1">{t('dni_label')} <span className="text-[#48293A]">*</span></label>
                <input value={dni} onChange={e => setDni(e.target.value)} className="input-manrique" required placeholder="12345678A" data-testid="scan-dni-input" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-manrique-black mb-1">{t('email_label')} <span className="text-[#48293A]">*</span></label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="input-manrique" required data-testid="scan-email-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-manrique-black mb-1">{t('password_label')} <span className="text-[#48293A]">*</span></label>
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="input-manrique" required data-testid="scan-password-input" />
                </div>
              </div>
            </div>

            <div className="card-manrique p-6 space-y-4">
              <h3 className="font-heading font-bold text-base text-manrique-black flex items-center gap-2">
                <Phone size={18} /> {t('phone_title')}
              </h3>
              {!phoneVerified ? (
                <>
                  <div className="flex gap-3">
                    <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+34 612 345 678" className="input-manrique flex-1" disabled={otpSent} data-testid="scan-phone-input" />
                    {!otpSent && (
                      <button type="button" onClick={handleSendOtp} disabled={sendingOtp || otpCooldown > 0} className="btn-manrique whitespace-nowrap text-sm" data-testid="scan-send-otp">
                        {sendingOtp ? t('sending') : otpCooldown > 0 ? `Reenviar en ${otpCooldown}s` : 'Validar SMS'}
                      </button>
                    )}
                  </div>
                  {otpSent && (
                    <div className="space-y-3 animate-fade-in">
                      {debugCode && (
                        <div className="bg-[#48293A]/5 border border-[#48293A]/20 rounded-2xl p-3 text-center">
                          <p className="text-xs text-manrique-grey">{t('test_code')}:</p>
                          <p className="font-heading font-bold text-xl text-[#48293A]" data-testid="scan-debug-code">{debugCode}</p>
                        </div>
                      )}
                      <div className="flex justify-center">
                        <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="scan-otp-input">
                          <InputOTPGroup>
                            {[0,1,2,3,4,5].map(i => (
                              <InputOTPSlot key={i} index={i} className="w-11 h-12 text-lg rounded-xl border-black/10" />
                            ))}
                          </InputOTPGroup>
                        </InputOTP>
                      </div>
                      <button type="button" onClick={handleVerifyOtp} disabled={sendingOtp || otp.length < 6} className="btn-manrique w-full text-sm" data-testid="scan-verify-otp">
                        {sendingOtp ? t('verifying') : t('verify_code')}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-center gap-3 bg-[#27AE60]/10 rounded-2xl p-4" data-testid="scan-phone-verified">
                  <div className="w-8 h-8 rounded-full bg-[#27AE60] flex items-center justify-center">
                    <Check size={16} className="text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-manrique-black text-sm">{t('phone_verified')}</p>
                    <p className="text-xs text-manrique-grey">{phone}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="card-manrique p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-heading font-bold text-lg text-manrique-black flex items-center gap-2">
                  <Users size={20} /> {t('children_label')}
                </h3>
                <span className="text-xs text-manrique-muted">{children.length}/3 max</span>
              </div>
              <div className="space-y-4">
                {children.map((child, i) => (
                  <div key={i} className="bg-[#FBF8F3] rounded-2xl p-4 space-y-3" data-testid={`scan-child-card-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-heading font-bold text-sm text-[#48293A]">
                        {i === 0 ? '1er' : i === 1 ? '2do' : '3er'} Hijo/a
                      </span>
                      {children.length > 1 && (
                        <button type="button" onClick={() => removeChild(i)} className="p-1.5 text-manrique-muted hover:text-[#48293A] rounded-full hover:bg-[#48293A]/5">
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-manrique-grey mb-1">Nombre completo</label>
                      <input value={child.name} onChange={e => updateChild(i, 'name', e.target.value)} placeholder="Nombre y apellidos del menor" className="input-manrique text-base" data-testid={`scan-child-name-${i}`} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-manrique-grey mb-1">{t('child_age')}</label>
                      <input type="number" value={child.age} onChange={e => updateChild(i, 'age', e.target.value)} placeholder="Edad" min="0" max="13" className="input-manrique w-32 text-base" data-testid={`scan-child-age-${i}`} />
                    </div>
                  </div>
                ))}
              </div>
              {children.length < 3 && (
                <button type="button" onClick={addChild} className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border-2 border-dashed border-[#48293A]/30 text-[#48293A] font-medium text-sm hover:bg-[#48293A]/5 transition-colors" data-testid="scan-add-child">
                  <Plus size={18} /> Registrar otro hijo/a
                </button>
              )}
            </div>

            <button
              type="submit"
              disabled={submitting || !phoneVerified}
              className="btn-manrique w-full flex items-center justify-center gap-2 py-4 text-base disabled:opacity-40"
              data-testid="scan-register-submit"
            >
              {submitting
                ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <>{t('next')} - {t('step_pool')} <ArrowRight size={18} /></>
              }
            </button>
          </form>

          <div className="mt-4 text-center">
            <button onClick={() => setPhase('phone_recognize')} className="text-sm text-[#48293A] hover:underline font-medium" data-testid="scan-go-to-login">
              {t('toggle_to_login')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── LOGIN (email + password) ──────────────────────────────────────────────
  if (phase === 'login') {
    return (
      <div className="min-h-screen bg-[#FBF8F3] flex items-center justify-center p-4" data-testid="scan-login">
        <div className="w-full max-w-md">
          <Header />
          <div className="card-manrique p-8">
            <h2 className="font-heading font-bold text-2xl text-manrique-black mb-1">{t('login_title')}</h2>
            <p className="text-manrique-grey text-sm mb-6">{t('login_subtitle')}</p>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-manrique-black mb-1">{t('email_label')}</label>
                <input type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} className="input-manrique" required data-testid="scan-login-email" />
              </div>
              <div>
                <label className="block text-sm font-medium text-manrique-black mb-1">{t('password_label')}</label>
                <input type="password" value={loginPassword} onChange={e => setLoginPassword(e.target.value)} className="input-manrique" required data-testid="scan-login-password" />
              </div>
              <button type="submit" disabled={submitting} className="btn-manrique w-full flex items-center justify-center gap-2" data-testid="scan-login-submit">
                {submitting ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <>{t('login_button')} <ArrowRight size={18} /></>}
              </button>
            </form>
            <div className="mt-4 text-center">
              <button onClick={() => setPhase('phone_recognize')} className="text-sm text-[#48293A] hover:underline font-medium" data-testid="scan-go-to-register">
                {t('toggle_to_register')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── WORKSHOPS (Today's activities) ────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#FBF8F3]" data-testid="scan-workshops">
      <header className="bg-white border-b border-black/5 px-4 sm:px-8 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Logo" className="h-9 w-auto" />
            <div>
              <p className="text-xs text-manrique-grey">{t('welcome')}, {family?.family_name}</p>
              <p className="text-[11px] text-manrique-muted">{family?.room_number}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSelector variant="minimal" />
            <button onClick={goToDashboard} className="text-xs text-[#48293A] hover:underline font-medium" data-testid="go-to-dashboard">
              Mi Perfil
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-8 py-6 space-y-6">
        <div className="card-manrique p-5 flex items-center justify-between" data-testid="daily-limit-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#48293A]/10 flex items-center justify-center">
              <Calendar size={20} className="text-[#48293A]" />
            </div>
            <div>
              <p className="font-heading font-bold text-base text-manrique-black">
                {t('tab_workshops')} - {new Date().toLocaleDateString()}
              </p>
              <p className="text-xs text-manrique-muted">
                {reservationsToday}/{dailyLimit} {t('reserved').toLowerCase()}
              </p>
            </div>
          </div>
          <div className="flex gap-1">
            {Array.from({ length: dailyLimit }, (_, i) => (
              <div
                key={i}
                className={`w-3 h-3 rounded-full ${i < reservationsToday ? 'bg-[#48293A]' : 'bg-black/10'}`}
              />
            ))}
          </div>
        </div>

        {!canBook && (
          <div className="bg-[#A8752B]/10 border border-[#A8752B]/20 rounded-2xl p-4 text-center" data-testid="limit-reached-banner">
            <p className="text-sm font-medium text-[#A8752B]">
              Has alcanzado el limite de {dailyLimit} {dailyLimit === 1 ? 'taller' : 'talleres'} por dia
            </p>
          </div>
        )}

        {/* Banner: todas las actividades reservables están llenas */}
        {(() => {
          const reservable = workshops.filter(w => w.registration_type !== 'free');
          const allFull = reservable.length > 0 && reservable.every(w => w.current_count >= w.max_capacity);
          if (!allFull) return null;
          return (
            <div className="bg-[#48293A]/8 border border-[#48293A]/20 rounded-2xl p-4" data-testid="all-full-banner">
              <p className="text-sm font-semibold text-[#48293A] text-center">
                Las plazas de hoy están completas. ¡Te esperamos otro día!
              </p>
              <p className="text-xs text-manrique-muted text-center mt-1">
                Puedes seguir accediendo a las actividades de acceso libre.
              </p>
            </div>
          );
        })()}

        <div className="space-y-3" data-testid="today-workshops-list">
          {workshops.length === 0 ? (
            <div className="card-manrique text-center py-12 text-manrique-muted">
              <Calendar size={32} className="mx-auto mb-3 opacity-30" />
              <p className="font-heading font-bold text-base text-manrique-grey">{t('no_workshops_day')}</p>
            </div>
          ) : (
            workshops.map(w => {
              const isFree   = w.registration_type === 'free';
              const full     = !isFree && w.current_count >= w.max_capacity;
              const unit     = w.capacity_unit === 'palos' ? 'palos' : t('capacity');
              const normsData = ACTIVITY_NORMS[w.title];
              return (
                <div key={w.id} className="card-manrique p-5" data-testid={`scan-workshop-${w.id}`}>
                  <div className="flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-heading font-bold text-base text-manrique-black">{w.title}</h3>
                        {isFree && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#27AE60]/10 text-[#27AE60]">Acceso libre</span>
                        )}
                      </div>
                      <p className="text-sm text-manrique-grey mt-0.5">{w.description}</p>
                      {normsData && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {normsData.stats.slice(0, 3).map(s => (
                            <span key={s.label} className="text-[10px] bg-black/5 text-manrique-black px-2 py-0.5 rounded-full">
                              {s.label}: <strong>{s.value}</strong>
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-xs text-manrique-muted flex-wrap">
                        <span className="flex items-center gap-1"><Clock size={12} /> {w.time_start} - {w.time_end}</span>
                        {!isFree && <span className="flex items-center gap-1"><Users size={12} /> {w.current_count}/{w.max_capacity} {unit}</span>}
                        <span>{t('ages')}: {w.age_min}-{w.age_max}</span>
                        {normsData && (
                          <button
                            onClick={() => setNormsOpen({ title: w.title, norms: normsData })}
                            className="flex items-center gap-1 text-[#48293A] font-medium hover:underline"
                          >
                            <BookOpen size={11} /> Ver normas
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      {isFree ? (
                        <span className="text-sm font-medium text-[#27AE60] bg-[#27AE60]/8 px-4 py-2 rounded-full whitespace-nowrap">Sin reserva</span>
                      ) : full ? (
                        <span className="text-sm font-medium text-[#48293A] bg-[#48293A]/5 px-4 py-2 rounded-full whitespace-nowrap">{t('full')}</span>
                      ) : !canBook ? (
                        <span className="text-sm text-manrique-muted px-4 py-2 rounded-full bg-black/5 whitespace-nowrap">Límite</span>
                      ) : (
                        <button
                          onClick={() => { setBookingId(w.id); setSelectedChildIds([]); }}
                          className="btn-manrique text-sm py-2 px-5 flex items-center gap-1"
                          data-testid={`scan-book-${w.id}`}
                        >
                          {t('book')} <ChevronRight size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Actividades de acceso libre — siempre disponibles */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 px-1">
            <div className="flex-1 h-px bg-black/8" />
            <p className="text-xs font-semibold text-manrique-muted uppercase tracking-widest whitespace-nowrap">Acceso libre todo el día</p>
            <div className="flex-1 h-px bg-black/8" />
          </div>
          {FREE_ACCESS_ACTIVITIES.map(act => {
            const norms = FREE_ACCESS_NORMS[act.normsKey];
            return (
              <div key={act.id} className="card-manrique p-5">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-heading font-bold text-base text-manrique-black">{act.title}</h3>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#27AE60]/10 text-[#27AE60]">Sin reserva</span>
                    </div>
                    <p className="text-xs text-manrique-muted mt-0.5">{act.subtitle}</p>
                    {norms && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {norms.stats.slice(0, 3).map(s => (
                          <span key={s.label} className="text-[10px] bg-black/5 text-manrique-black px-2 py-0.5 rounded-full">
                            {s.label}: <strong>{s.value}</strong>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  {norms && (
                    <button
                      onClick={() => setNormsOpen({ title: act.title, norms })}
                      className="flex-shrink-0 flex items-center gap-1.5 text-sm font-medium text-[#48293A] border border-[#48293A]/20 px-3 py-1.5 rounded-2xl hover:bg-[#48293A]/5 transition-colors"
                    >
                      <BookOpen size={14} /> Normas
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </main>

      <NormsModal
        open={!!normsOpen}
        onClose={() => setNormsOpen(null)}
        title={normsOpen?.title}
        norms={normsOpen?.norms}
      />

      {bookingId && bookingWorkshop && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="scan-booking-modal">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading font-bold text-xl text-manrique-black">{t('select_children')}</h3>
              <button onClick={() => setBookingId(null)} className="p-1 hover:bg-[#FBF8F3] rounded-full">
                <X size={20} />
              </button>
            </div>
            <p className="text-sm text-manrique-grey mb-5">
              {bookingWorkshop.title} · {bookingWorkshop.time_start}–{bookingWorkshop.time_end}
            </p>
            <div className="space-y-3 mb-6">
              {family?.children?.map((child, i) => {
                const disabled = isChildDisabled(child);
                const checked  = selectedChildIds.includes(child.id);
                return (
                  <label
                    key={child.id || child.name}
                    className={`flex items-center gap-3 p-4 rounded-2xl border transition-all ${
                      disabled
                        ? 'border-black/5 bg-black/3 cursor-not-allowed opacity-60'
                        : checked
                          ? 'border-[#48293A] bg-[#48293A]/5 cursor-pointer'
                          : 'border-black/10 hover:border-black/20 cursor-pointer'
                    }`}
                    data-testid={`scan-select-child-${i}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled}
                      onChange={() => !disabled && toggleChildId(child.id)}
                      className="w-5 h-5 rounded accent-[#48293A]"
                    />
                    <div className="flex-1 min-w-0">
                      <span className="text-manrique-black font-medium">{child.name}</span>
                      {disabled && (
                        <p className="text-[10px] text-[#A8752B] mt-0.5">
                          Ya apuntado/a a «{bookingWorkshop.title}» hoy
                        </p>
                      )}
                    </div>
                    <span className="text-sm text-manrique-muted">{child.age} {t('years')}</span>
                  </label>
                );
              })}
            </div>
            <button
              onClick={handleBook}
              disabled={selectedChildIds.length === 0}
              className="btn-manrique w-full disabled:opacity-40"
              data-testid="scan-confirm-booking"
            >
              {t('confirm_booking')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
